import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import GaHelper from "lib/gaHelper";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";

const UNIT_LENGTH = config.outfitList.LENGTH;
const EXP_MINUTE = 30;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_NOW_FOLDER_HASH_AND_VALUE = "pick/SET_NOW_FOLDER_HASH_AND_VALUE";
const SET_OUTFIT_LIST = "pick/SET_OUTFIT_LIST";
const SET_FOLDER_LIST = "pick/SET_FOLDER_LIST";
const CHECK_IS_END = "pick/CHECK_IS_END";
const SET_EXPIRATION_DATE = "pick/SET_EXPIRATION_DATE";
const SET_IS_LOADING = "pick/SET_IS_LOADING";
const EXPORT_PICK_DATA = "pick/EXPORT_PICK_DATA";
const INIT_EXPORTED_LIST = "pick/INIT_EXPORTED_LIST";
const INIT_FOLDER_OBJ = "pick/INIT_FOLDER_OBJ";
const INIT_ALL_DATA = "pick/INIT_ALL_DATA";

const setNowFolderHashAndValue = createAction(SET_NOW_FOLDER_HASH_AND_VALUE);
const setOutfitList = createAction(SET_OUTFIT_LIST);
const setFolderList = createAction(SET_FOLDER_LIST);
const checkIsEnd = createAction(CHECK_IS_END);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const setIsLoading = createAction(SET_IS_LOADING);
const exportPickData = createAction(EXPORT_PICK_DATA);
const initExportedList = createAction(INIT_EXPORTED_LIST);
const initFolderObj = createAction(INIT_FOLDER_OBJ);
const initAllData = createAction(INIT_ALL_DATA);

const getPickState = ({ getState }) => {
  const pickState = getState().pick.toJS();
  return pickState;
};

const getIsLoading = ({ getState }) => {
  const pickState = getPickState({ getState });
  const { isLoading } = pickState;
  return isLoading;
};

const getLastHash = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["outfitHash"];
};

const getTargetData = ({ getState, hash }) => {
  const pickState = getPickState({ getState });
  return pickState.pickObj[hash];
};

const checkExpDate = ({ getState, hash }) => {
  const targetData = getTargetData({ getState, hash });
  const expirationDate = targetData && targetData.expirationDate;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch, hash }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate({ expirationDate, hash }));
};

const setNowFolder = ({ getState, dispatch, hash }) => {
  const pickState = getPickState({ getState });
  const { pickFolderList } = pickState;

  let name = "";

  for (let i = 0; i < pickFolderList.length; i++) {
    const element = pickFolderList[i];
    if (element.hash === hash) {
      name = element.name;
      break;
    }
  }

  dispatch(setNowFolderHashAndValue({ hash, name }));
};

const requestOutfitList = async ({ savedList, hash, idToken }) => {
  const length = UNIT_LENGTH;
  const lastHash = getLastHash(savedList);
  if (hash === "all") {
    const { code, res, errInfo } = await Restful.getPickListAPI({
      idToken,
      lastHash,
      length
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else {
      return { list: res.list };
    }
  } else {
    const { code, res, errInfo } = await Restful.getFolderPickListAPI({
      idToken,
      pickFolderHash: hash,
      lastHash,
      length
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else {
      return { list: res.list };
    }
  }
};

const addNewList = async ({ hash, idToken, dispatch, getState }) => {
  const { savedList } = getTargetData({ getState, hash });
  const { list } = await requestOutfitList({ savedList, hash, idToken });
  dispatch(setOutfitList({ hash, savedList: savedList.concat(list) }));
  dispatch(checkIsEnd({ hash, length: list.length }));
  GaHelper.setGARequestOutfitListEvent({
    type: "pickOutfitList",
    pageNum: savedList.length / UNIT_LENGTH + 1
  });
};

const getNowFolder = ({ getState }) => {
  const pickState = getPickState({ getState });
  const { nowPickFolderHash, nowPickFolderName } = pickState;
  return { nowHash: nowPickFolderHash, nowName: nowPickFolderName };
};

const getFirstList = async ({ hash, idToken, dispatch, getState }) => {
  let isLoading = getIsLoading({ getState });

  while (isLoading) {
    await sleep(WAIT_MILLISEC);
    isLoading = getIsLoading({ getState });
  }

  dispatch(setIsLoading(true));

  setNowFolder({ getState, dispatch, hash });

  const { needUpdate } = checkExpDate({ getState, hash });

  if (needUpdate) {
    dispatch(initExportedList());
    dispatch(initFolderObj(hash));
    try {
      await addNewList({ hash, idToken, dispatch, getState });
    } catch (error) {
      dispatch(setIsLoading(false));
      throw error;
    }
  }

  const { nowHash } = getNowFolder({ getState });
  if (nowHash === hash) {
    dispatch(exportPickData(hash));
  }

  if (needUpdate) {
    setExpDate({ dispatch, hash });
  }

  dispatch(setIsLoading(false));
};

const getFolderList = async ({ idToken, dispatch, getState }) => {
  const { code, res, errInfo } = await Restful.getPickFolderListAPI({
    idToken
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { list } = res;
  dispatch(setFolderList({ list }));
};

const writeNewFolderNameToBackend = async ({
  folderName,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.setPickFolderAPI({
    idToken,
    data: { folderName: folderName.trim() }
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { needCreative, success, name, hash, list } = res;
  dispatch(setFolderList({ list }));

  return { needCreative, success, name, hash };
};

const changeFolderNameToBackend = async ({
  newFolderName,
  folderHash,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.editPickFolderAPI({
    idToken,
    data: { folderName: newFolderName.trim() },
    pickFolderHash: folderHash
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { success, /*name, hash,*/ list } = res;
  dispatch(setFolderList({ list }));

  return { success };
};

const deleteFolderNameToBackend = async ({
  folderHash,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.removePickFolderAPI({
    idToken,
    pickFolderHash: folderHash
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { success, list } = res;
  dispatch(setFolderList({ list }));

  return { success };
};

export const initOutfitList = idToken => async (dispatch, getState) => {
  const { nowHash } = getNowFolder({ getState });
  if (!!nowHash) return null;

  await Promise.all([
    getFirstList({ hash: "all", idToken, dispatch, getState }),
    getFolderList({ idToken, dispatch, getState })
  ]);
};

export const getOutfitList = idToken => async (dispatch, getState) => {
  const pickState = getPickState({ getState });
  const { isEnd } = pickState;
  const isLoading = getIsLoading({ getState });
  const { nowHash: hash } = getNowFolder({ getState });

  if (!hash) throw Error("null hash");
  if (isLoading || isEnd) return null;

  dispatch(setIsLoading(true));
  try {
    await addNewList({ hash, idToken, dispatch, getState });
  } catch (error) {
    dispatch(setIsLoading(false));
    throw error;
  }

  const { nowHash } = getNowFolder({ getState });
  if (nowHash === hash) {
    dispatch(exportPickData(hash));
  }

  dispatch(setIsLoading(false));
};

export const changeOutfitListOfFolder =
  ({ idToken, folderHash }) =>
  async (dispatch, getState) => {
    await getFirstList({ hash: folderHash, idToken, dispatch, getState });
  };

export const refreshFolderList =
  ({ idToken }) =>
  async (dispatch, getState) => {
    getFolderList({ idToken, dispatch, getState });
  };

export const createPickFolder =
  ({ folderName, idToken }) =>
  async (dispatch, getState) => {
    const { needCreative, success, name, hash } =
      await writeNewFolderNameToBackend({
        folderName,
        idToken,
        dispatch,
        getState
      });

    return { needCreative, success, name, hash };
  };

export const editPickFolder =
  ({ folderHash, newFolderName, idToken }) =>
  async (dispatch, getState) => {
    const { success } = await changeFolderNameToBackend({
      newFolderName,
      folderHash,
      idToken,
      dispatch,
      getState
    });
    return { success };
  };

export const deletePickFolder =
  ({ folderHash, idToken }) =>
  async (dispatch, getState) => {
    const { success } = await deleteFolderNameToBackend({
      folderHash,
      idToken,
      dispatch,
      getState
    });
    return { success };
  };

export const removeOldSavedList =
  ({ hash }) =>
  (dispatch, getState) => {
    dispatch(initFolderObj(hash));
  };

export const removeExportedList = () => async (dispatch, getState) => {
  dispatch(setNowFolderHashAndValue({ hash: null, name: null }));
  dispatch(initExportedList());
};

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  pickObj: {},
  /*
  pickObj 의 구조.
  pickObj: {
    all: {
      savedList: [],
      expirationDate: null,
      isEnd: false
    },
    hash1: {
      savedList: [],
      expirationDate: null,
      isEnd: false
    }
    ...
  },
  */

  pickFolderList: [{ hash: "all", name: "all" }],

  nowPickFolderHash: null,
  nowPickFolderName: null,

  outfitList: [],
  isEnd: false,
  isLoading: false
});

export default handleActions(
  {
    [SET_NOW_FOLDER_HASH_AND_VALUE]: (state, action) => {
      const { hash, name } = action.payload;
      return state
        .set("nowPickFolderHash", fromJS(hash))
        .set("nowPickFolderName", fromJS(name));
    },
    [SET_OUTFIT_LIST]: (state, action) => {
      const { hash, savedList } = action.payload;
      return state.setIn(["pickObj", hash, "savedList"], fromJS(savedList));
    },
    [SET_FOLDER_LIST]: (state, action) => {
      const { list } = action.payload;
      const { pickFolderList } = initialState.toJS();
      const newList = pickFolderList.concat(list);
      return state.set("pickFolderList", fromJS(newList));
    },
    [CHECK_IS_END]: (state, action) => {
      const { hash, length } = action.payload;
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.setIn(["pickObj", hash, "isEnd"], fromJS(isEnd));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const { hash, expirationDate } = action.payload;
      return state.setIn(
        ["pickObj", hash, "expirationDate"],
        fromJS(expirationDate)
      );
    },
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [EXPORT_PICK_DATA]: (state, action) => {
      const hash = action.payload;
      const { savedList, isEnd } = state.toJS().pickObj[hash];
      return state
        .set("outfitList", fromJS(savedList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_EXPORTED_LIST]: (state, action) => {
      const { outfitList, isEnd } = initialState.toJS();
      return state
        .set("outfitList", fromJS(outfitList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_FOLDER_OBJ]: (state, action) => {
      const hash = action.payload;
      const initObj = {
        savedList: [],
        expirationDate: null,
        isEnd: false
      };
      return state.setIn(["pickObj", hash], fromJS(initObj));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
